import React, { useState, useRef, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { validateEmail } from "data/mockData";
import { fetchTenants, transformTenantsData, deleteTenant, updateTenantStatus, updateTenantPics } from "action/tenantAction";

/* ── Icons ── */
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
  </svg>
);
const CogIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/>
  </svg>
);
const MoreVIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/>
  </svg>
);
const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const UsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
  </svg>
);
const ChevronRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

function Badge({ variant, noDot, children }) {
  return (
    <span className={`lms-badge lms-badge-${variant}${noDot ? " lms-badge-no-dot" : ""}`}>
      {children}
    </span>
  );
}

function TenantMark({ name, index }) {
  const COLORS = ["#4F46E5","#0EA5E9","#10B981","#F59E0B","#EF4444","#8B5CF6","#EC4899","#14B8A6"];
  const bg = COLORS[index % COLORS.length];
  const initials = (name || "T").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return <div className="lms-tenant-mark" style={{ background: bg }}>{initials}</div>;
}

/* Simple dropdown menu */
function RowMenu({ tenant, onView, onEdit, onDelete, onDeactivate, onManagePics, onSettingMenu }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const close = () => setOpen(false);
  return (
    <div className="lms-dropdown-wrapper" ref={ref}>
      <button className="lms-btn lms-btn-ghost lms-btn-sm lms-btn-icon" onClick={() => setOpen((o) => !o)}>
        <MoreVIcon />
      </button>
      {open && (
        <div className="lms-dropdown" style={{ right: 0, minWidth: 180 }}>
          <button className="lms-dropdown-item" onClick={() => { close(); onView(tenant); }}><EyeIcon /> {t("View Details")}</button>
          <button className="lms-dropdown-item" onClick={() => { close(); onEdit(tenant); }}><EditIcon /> {t("Edit Tenant")}</button>
          <button className="lms-dropdown-item" onClick={() => { close(); onManagePics(tenant); }}><UsersIcon /> {t("Manage PIC")}</button>
          <button className="lms-dropdown-item" onClick={() => { close(); onSettingMenu(tenant.id); }}><CogIcon /> {t("Setting Menu")}</button>
          <div className="lms-dropdown-divider" />
          <div className="lms-dropdown-label">{t("Admin Actions")}</div>
          <button className="lms-dropdown-item danger" onClick={() => { close(); onDeactivate(tenant.id); }}>{t("Deactivate")}</button>
          <button className="lms-dropdown-item danger" onClick={() => { close(); onDelete(tenant.id); }}><TrashIcon /> {t("Delete Tenant")}</button>
        </div>
      )}
    </div>
  );
}

/* Confirm dialog */
function ConfirmDialog({ open, title, message, confirmLabel, confirmVariant = "danger", loading, onConfirm, onCancel }) {
  const { t } = useTranslation();
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", backdropFilter: "blur(4px)", zIndex: 90, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0 24px 48px -12px rgba(15,23,42,0.18)", width: "100%", maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid var(--border-subtle)" }}>
          <div className="t-h2">{title}</div>
        </div>
        <div style={{ padding: "16px 24px" }}><div className="t-body">{message}</div></div>
        <div style={{ padding: "14px 24px", borderTop: "1px solid var(--border-subtle)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="lms-btn lms-btn-secondary" onClick={onCancel} disabled={loading}>{t("Cancel")}</button>
          <button className={`lms-btn lms-btn-${confirmVariant === "danger" ? "danger" : "primary"}`} onClick={onConfirm} disabled={loading}>
            {loading && <span className="lms-spinner" style={{ width: 14, height: 14 }} />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* PIC Management Modal */
function PicModal({ open, onClose, tenant, onSave }) {
  const { t } = useTranslation();
  const [pics, setPics] = useState([]);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (tenant?.pics) setPics(tenant.pics);
    else setPics([{ id: Date.now(), nama: "", nomorTelepon: "", email: "" }]);
    setErrors([]);
  }, [tenant]);

  const handleChange = (idx, field, value) => {
    setPics((p) => p.map((pic, i) => i === idx ? { ...pic, [field]: value } : pic));
  };
  const addPic = () => setPics((p) => [...p, { id: Date.now(), nama: "", nomorTelepon: "", email: "" }]);
  const removePic = (idx) => { if (pics.length > 1) setPics((p) => p.filter((_, i) => i !== idx)); };

  const validate = () => {
    const errs = pics.map(() => ({ nama: "", nomorTelepon: "", email: "" }));
    let valid = true;
    pics.forEach((pic, i) => {
      if (!pic.nama.trim()) { errs[i].nama = t("Nama PIC wajib"); valid = false; }
      if (!pic.nomorTelepon.trim()) { errs[i].nomorTelepon = t("Nomor telepon wajib"); valid = false; }
      if (!pic.email.trim()) { errs[i].email = t("Email wajib"); valid = false; }
      else if (!validateEmail(pic.email)) { errs[i].email = t("Format email tidak valid"); valid = false; }
    });
    setErrors(errs);
    return valid;
  };

  if (!open || !tenant) return null;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)", zIndex: 90, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0 24px 48px -12px rgba(15,23,42,0.18)", width: "100%", maxWidth: 580, maxHeight: "90vh", display: "flex", flexDirection: "column" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div className="t-h2">Manage PIC — {tenant.namaTenant}</div>
          <button className="lms-btn lms-btn-ghost lms-btn-sm lms-btn-icon" onClick={onClose}><XIcon /></button>
        </div>
        <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 16 }}>
          {pics.map((pic, i) => (
            <div key={pic.id} className="lms-repeater-card">
              <div className="lms-repeater-head">
                <div className="lms-repeater-num">{i + 1}</div>
                <div className="t-h3">PIC #{i + 1}</div>
                {pics.length > 1 && (
                  <button className="lms-btn lms-btn-ghost lms-btn-sm lms-btn-icon" style={{ marginLeft: "auto", color: "var(--danger-500)" }} onClick={() => removePic(i)}><TrashIcon /></button>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div className="lms-field">
                  <label className="lms-field-label">{t("Nama PIC")} <span className="req">*</span></label>
                  <input className="lms-input" placeholder={t("Masukkan nama PIC")} value={pic.nama} onChange={(e) => handleChange(i, "nama", e.target.value)} />
                  {errors[i]?.nama && <div className="lms-field-error">{errors[i].nama}</div>}
                </div>
                <div className="lms-grid-2">
                  <div className="lms-field">
                    <label className="lms-field-label">{t("Nomor Telepon")} <span className="req">*</span></label>
                    <input className="lms-input" placeholder="08xxxxxxxxxx" value={pic.nomorTelepon} onChange={(e) => handleChange(i, "nomorTelepon", e.target.value)} />
                    {errors[i]?.nomorTelepon && <div className="lms-field-error">{errors[i].nomorTelepon}</div>}
                  </div>
                  <div className="lms-field">
                    <label className="lms-field-label">{t("Email")} <span className="req">*</span></label>
                    <input className="lms-input" type="email" placeholder="pic@example.com" value={pic.email} onChange={(e) => handleChange(i, "email", e.target.value)} />
                    {errors[i]?.email && <div className="lms-field-error">{errors[i].email}</div>}
                  </div>
                </div>
              </div>
            </div>
          ))}
          <button className="lms-btn lms-btn-secondary lms-btn-sm" onClick={addPic} style={{ alignSelf: "flex-start" }}>
            <PlusIcon /> {t("Tambah PIC")}
          </button>
        </div>
        <div style={{ padding: "14px 24px", borderTop: "1px solid var(--border-subtle)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="lms-btn lms-btn-secondary" onClick={onClose}>{t("Cancel")}</button>
          <button className="lms-btn lms-btn-primary" onClick={() => { if (validate()) onSave(pics); }}>{t("Save Changes")}</button>
        </div>
      </div>
    </div>
  );
}

const MONTHS_ID = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agt","Sep","Okt","Nov","Des"];
function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return `${d.getDate()} ${MONTHS_ID[d.getMonth()]} ${d.getFullYear()}`;
}

/* ── Main component ── */
function MasterTenantManagement() {
  const { t } = useTranslation();
  useEffect(() => { document.title = "Master Tenant Management | LMS Tenant"; }, []);

  const history = useHistory();
  const location = useLocation();
  const toast = useToast();
  const urlParams = new URLSearchParams(location.search);
  const urlFilter = urlParams.get("filter");

  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [filterNama, setFilterNama] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ current_page: 1, per_page: 15, total: 0, last_page: 1 });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [picOpen, setPicOpen] = useState(false);

  useEffect(() => {
    if (urlFilter === "active") setFilterStatus("active");
    else if (urlFilter === "expired") setFilterStatus("non_active");
  }, [urlFilter]);

  const loadTenants = React.useCallback(async () => {
    setLoading(true);
    try {
      const filters = { search: filterNama, status: filterStatus || "", per_page: 15 };
      if (filterStartDate) filters.expired_after = filterStartDate;
      if (filterEndDate) filters.expired_before = filterEndDate;
      const result = await fetchTenants(filters);
      if (result.success) {
        setTenants(transformTenantsData(result.data));
        setMeta(result.meta);
      } else {
        toast({ title: t("Error"), description: result.message || t("Failed to load tenants"), status: "error", duration: 3000, isClosable: true });
      }
    } catch {
      toast({ title: t("Error"), description: t("Failed to load tenants"), status: "error", duration: 3000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [filterNama, filterStatus, filterStartDate, filterEndDate, toast, t]);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  const confirmDelete = async () => {
    if (!selectedTenant) return;
    try {
      const result = await deleteTenant(selectedTenant.id);
      if (result.success) {
        toast({ title: t("Tenant Deleted"), description: t("Tenant berhasil dihapus."), status: "success", duration: 3000, isClosable: true });
        await loadTenants();
      } else {
        toast({ title: t("Error"), description: result.message || t("Failed to delete"), status: "error", duration: 3000, isClosable: true });
      }
    } catch {
      toast({ title: t("Error"), description: t("Failed to delete tenant"), status: "error", duration: 3000, isClosable: true });
    }
    setDeleteOpen(false);
  };

  const confirmDeactivate = async () => {
    if (!selectedTenant) return;
    try {
      const result = await updateTenantStatus(selectedTenant.id, "non_active");
      if (result.success) {
        toast({ title: t("Tenant Deactivated"), description: t("Tenant berhasil dinonaktifkan."), status: "warning", duration: 3000, isClosable: true });
        await loadTenants();
      } else {
        toast({ title: t("Error"), description: result.message || t("Failed to deactivate"), status: "error", duration: 3000, isClosable: true });
      }
    } catch {
      toast({ title: t("Error"), description: t("Failed to deactivate tenant"), status: "error", duration: 3000, isClosable: true });
    }
    setDeactivateOpen(false);
  };

  const handleSavePics = async (updatedPics) => {
    if (!selectedTenant) return;
    try {
      const result = await updateTenantPics(selectedTenant.id, updatedPics);
      if (result.success) {
        toast({ title: t("PIC Updated"), description: t("Informasi PIC berhasil diperbarui."), status: "success", duration: 3000, isClosable: true });
        await loadTenants();
      } else {
        toast({ title: t("Error"), description: result.message || t("Failed to update PICs"), status: "error", duration: 3000, isClosable: true });
      }
    } catch {
      toast({ title: t("Error"), description: t("Failed to update PICs"), status: "error", duration: 3000, isClosable: true });
    }
    setPicOpen(false);
  };

  const hasFilters = filterNama || filterStatus || filterStartDate || filterEndDate;

  const handleExport = () => {
    const rows = tenants.map((tn, i) => ({
      "No":                i + 1,
      "Nama Tenant":       tn.namaTenant || "-",
      "Domain":            tn.domainUnik || "-",
      "Email PIC":         tn.pics?.[0]?.email || tn.email || "-",
      "No. Telepon PIC":   tn.pics?.[0]?.nomorTelepon || tn.nomorTelepon || "-",
      "Status":            tn.status || "-",
      "Subscription":      tn.tanggalBerakhir && new Date(tn.tanggalBerakhir) >= new Date() ? "Active" : "Expired",
      "Paket":             tn.subscriptionPlan || "-",
      "Tanggal Mulai":     tn.tanggalMulai || "-",
      "Tanggal Berakhir":  tn.tanggalBerakhir || "-",
      "Diskon":            tn.diskon > 0 ? `${tn.diskon}%` : "-",
      "Total Harga":       tn.total && Number(tn.total) > 0 ? Number(tn.total) : 0,
    }));

    const ws = XLSX.utils.json_to_sheet(rows);

    // Column widths
    ws["!cols"] = [
      { wch: 5 }, { wch: 28 }, { wch: 28 }, { wch: 32 }, { wch: 18 },
      { wch: 12 }, { wch: 14 }, { wch: 18 }, { wch: 14 }, { wch: 14 },
      { wch: 10 }, { wch: 16 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Master Tenant");

    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `master-tenant-${date}.xlsx`);
  };

  return (
    <div className="lms-page lms-anim-fade">
      {/* Header */}
      <div className="lms-page-header">
        <div className="lms-page-header-text">
          <div className="lms-breadcrumb">
            <span>Apps</span><ChevronRightIcon /><span>Admin</span><ChevronRightIcon />
            <span className="lms-breadcrumb-current">{t("Master Tenant")}</span>
          </div>
          <div className="t-display">{t("Master Tenant")}</div>
          <div className="t-small">{t("Kelola tenant institusi, langganan, dan periode kontrak.")}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="lms-btn lms-btn-secondary" onClick={handleExport}><DownloadIcon /> {t("Export")}</button>
          <button className="lms-btn lms-btn-primary" onClick={() => history.push("/admin/create-tenant?mode=new")}>
            <PlusIcon /> {t("Add New Tenant")}
          </button>
        </div>
      </div>

      <div className="lms-card">
        {/* Filter grid */}
        <div className="lms-card-body" style={{ paddingBottom: 12 }}>
          <div className="lms-filter-grid">
            <div className="lms-field">
              <label className="lms-field-label">{t("Search")}</label>
              <div className="lms-input-wrap">
                <SearchIcon />
                <input className="lms-input" placeholder={t("Cari nama tenant…")} value={filterNama} onChange={(e) => setFilterNama(e.target.value)} />
              </div>
            </div>
            <div className="lms-field">
              <label className="lms-field-label">{t("Status")}</label>
              <select className="lms-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="">{t("Semua Status")}</option>
                <option value="active">Active</option>
                <option value="non_active">Non Active</option>
              </select>
            </div>
            <div className="lms-field">
              <label className="lms-field-label">{t("Start Date")}</label>
              <div className="lms-input-wrap">
                <CalendarIcon />
                <input className="lms-input" type="date" value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)} />
              </div>
            </div>
            <div className="lms-field">
              <label className="lms-field-label">{t("End Date")}</label>
              <div className="lms-input-wrap">
                <CalendarIcon />
                <input className="lms-input" type="date" value={filterEndDate} onChange={(e) => setFilterEndDate(e.target.value)} />
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <button className="lms-btn lms-btn-secondary" disabled={!hasFilters} onClick={() => { setFilterNama(""); setFilterStatus(""); setFilterStartDate(""); setFilterEndDate(""); }} style={{ width: "100%" }}>
                <XIcon /> {t("Clear Filters")}
              </button>
            </div>
          </div>
          {/* Active filter chips */}
          {hasFilters && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
              {filterNama && <span className="lms-chip">Name: {filterNama}<button onClick={() => setFilterNama("")}><XIcon /></button></span>}
              {filterStatus && <span className="lms-chip">Status: {filterStatus}<button onClick={() => setFilterStatus("")}><XIcon /></button></span>}
            </div>
          )}
        </div>

        {/* Table */}
        <div className="lms-table-wrap">
          <table className="lms-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>{t("No")}</th>
                <th>{t("Tenant")}</th>
                <th>{t("Contact")}</th>
                <th>{t("Status")}</th>
                <th>{t("Subscription")}</th>
                <th>{t("Plan")}</th>
                <th>{t("Period")}</th>
                <th>{t("Discount")}</th>
                <th style={{ textAlign: "right" }}>{t("Total Price")}</th>
                <th style={{ width: 52 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} style={{ textAlign: "center", padding: 48 }}>
                  <div className="lms-spinner" style={{ margin: "0 auto" }} />
                </td></tr>
              ) : tenants.length === 0 ? (
                <tr><td colSpan={10}>
                  <div className="lms-empty">
                    <div className="lms-empty-icon"><SearchIcon /></div>
                    <div className="t-h3">{t("Tidak ada tenant ditemukan")}</div>
                    <div className="t-small">{t("Coba ubah filter atau tambah tenant baru.")}</div>
                  </div>
                </td></tr>
              ) : tenants.map((tn, i) => (
                <tr key={tn.id}>
                  <td style={{ color: "var(--text-muted)", fontSize: 13 }}>{i + 1}</td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <TenantMark name={tn.namaTenant} index={i} />
                      <div style={{ minWidth: 0 }}>
                        <div className="td-strong" style={{ lineHeight: 1.3 }}>{tn.namaTenant}</div>
                        <div style={{ fontFamily: "monospace", fontSize: 11.5, color: "var(--text-muted)", marginTop: 2, lineHeight: 1.2 }}>{tn.domainUnik}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-strong)", lineHeight: 1.3 }}>{tn.pics?.[0]?.email || tn.email || "-"}</div>
                    {(tn.pics?.[0]?.nomorTelepon || tn.nomorTelepon) && (
                      <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{tn.pics?.[0]?.nomorTelepon || tn.nomorTelepon}</div>
                    )}
                  </td>
                  <td>
                    <Badge variant={tn.status === "Active" ? "success" : "neutral"}>
                      {tn.status === "Active" ? t("Active") : t("Inactive")}
                    </Badge>
                  </td>
                  <td>
                    {(() => {
                      const isActive = tn.tanggalBerakhir && new Date(tn.tanggalBerakhir) >= new Date();
                      return (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                          <Badge variant={isActive ? "success" : "warning"}>{isActive ? t("Active") : t("Expired")}</Badge>
                          {tn.gracePeriod > 0 && (
                            <span style={{ fontSize: 11, fontWeight: 600, background: "var(--warning-50)", color: "var(--warning-700)", borderRadius: 6, padding: "2px 7px", lineHeight: 1.4 }}>
                              Grace {tn.gracePeriod}h
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td><Badge variant="brand" noDot>{tn.subscriptionPlan || "Basic"}</Badge></td>
                  <td>
                    {tn.tanggalMulai || tn.tanggalBerakhir ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 12.5, color: "var(--text-muted)" }}>
                        <span>{fmtDate(tn.tanggalMulai) || "—"}</span>
                        <span style={{ color: "var(--text-xmuted, var(--text-muted))", opacity: 0.7 }}>↓ {fmtDate(tn.tanggalBerakhir) || "—"}</span>
                      </div>
                    ) : <span style={{ color: "var(--text-muted)" }}>—</span>}
                  </td>
                  <td>{tn.diskon > 0 ? <Badge variant="purple" noDot>{tn.diskon}%</Badge> : <span style={{ color: "var(--text-muted)" }}>—</span>}</td>
                  <td style={{ textAlign: "right" }} className="td-strong">
                    {tn.total && Number(tn.total) > 0 ? (
                      <span>Rp {Number(tn.total).toLocaleString("id-ID")}</span>
                    ) : <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>{t("Free")}</span>}
                  </td>
                  <td>
                    {tn.id !== 1 && (
                      <RowMenu
                        tenant={tn}
                        onView={(tv) => history.push(`/admin/tenant-detail/${tv.id}`)}
                        onEdit={(tv) => history.push(`/admin/edit-tenant/${tv.id}`)}
                        onDelete={(id) => { setSelectedTenant(tenants.find((x) => x.id === id)); setDeleteOpen(true); }}
                        onDeactivate={(id) => { setSelectedTenant(tenants.find((x) => x.id === id)); setDeactivateOpen(true); }}
                        onManagePics={(tv) => { setSelectedTenant(tv); setPicOpen(true); }}
                        onSettingMenu={(id) => history.push(`/admin/tenant-menu-setting/${id}`)}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="lms-pagination">
          <span className="lms-pagination-info">
            Showing <strong>{tenants.length}</strong> of <strong>{meta.total || tenants.length}</strong> {t("tenants")}
          </span>
        </div>
      </div>

      {/* Dialogs */}
      <ConfirmDialog
        open={deleteOpen}
        title={t("Delete Tenant")}
        message={<>Apakah Anda yakin ingin menghapus <strong>"{selectedTenant?.namaTenant}"</strong>? {t("Tindakan ini tidak dapat dibatalkan.")}</>}
        confirmLabel={t("Delete")}
        confirmVariant="danger"
        loading={false}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteOpen(false)}
      />
      <ConfirmDialog
        open={deactivateOpen}
        title={t("Deactivate Tenant")}
        message={<>Apakah Anda yakin ingin menonaktifkan <strong>"{selectedTenant?.namaTenant}"</strong>?</>}
        confirmLabel={t("Deactivate")}
        confirmVariant="danger"
        loading={false}
        onConfirm={confirmDeactivate}
        onCancel={() => setDeactivateOpen(false)}
      />
      <PicModal
        open={picOpen}
        onClose={() => setPicOpen(false)}
        tenant={selectedTenant}
        onSave={handleSavePics}
      />
    </div>
  );
}

export default MasterTenantManagement;
